import { useMemo, useCallback, useState } from "react";
import TestimonialList from "@/components/widgets/testimonial-list";
import VideoModal from "@/modules/review/components/video-modal";
import { useWidgetFont } from "../hooks/use-widget-font";
import type { WidgetTemplateProps } from "./template-router";

const sampleTestimonials = [
	{
		id: "1",
		name: "Sarah Chen",
		role: "Product Manager",
		company: "TechFlow",
		rating: 5,
		content: "This platform has completely transformed how we collect and showcase customer feedback. The wall of love feature is a game-changer for our marketing efforts!",
	},
	{
		id: "2",
		name: "Michael Roberts",
		role: "CEO",
		company: "StartupX",
		rating: 5,
		content: "Absolutely love it! Our conversion rates increased by 40% after adding the testimonial widget to our landing page.",
	},
	{
		id: "3",
		name: "Emily Watson",
		role: "Marketing Director",
		company: "GrowthLabs",
		rating: 5,
		content: "The best testimonial management tool I've ever used. Clean interface, powerful features, and excellent support team.",
	},
];

export default function TemplateClassicList({ previewData, layoutConfig, templateDetail, brandKit, showBranding = true, nextCursor, onLoadMore, isLoadingMore = false }: WidgetTemplateProps) {
	const fontStyle = useWidgetFont(layoutConfig, templateDetail);
	const [activeVideoPlaybackId, setActiveVideoPlaybackId] = useState<string | null>(null);

	const getLayoutValue = useCallback(
		(key: string, fallback: unknown = "") => {
			if (layoutConfig[key] !== undefined) return layoutConfig[key];
			if (templateDetail?.defaults?.layout?.[key] !== undefined) return templateDetail.defaults.layout[key];
			return fallback;
		},
		[layoutConfig, templateDetail],
	);

	const testimonials = useMemo(() => {
		if (!previewData) return sampleTestimonials;
		if (!("reviews" in previewData) || !previewData.reviews || previewData.reviews.data.length === 0) return sampleTestimonials;
		return previewData.reviews.data.map((review) => ({
			id: review.id,
			name: review.customer_name,
			role: review.customer_job_title,
			consent: review.consent,
			company: review.customer_company_name || "",
			rating: review.rating || 5,
			content: review.content_text || "",
			avatarUrl: review.customer_avatar_url || undefined,
			contentType: review.content_type || undefined,
			muxPlaybackId: review.mux_playback_id || undefined,
			videoThumbnailUrl: review.video_thumbnail_url || undefined,
			audioUrl: review.audio_url || undefined,
			audioDuration: review.audio_duration,
		}));
	}, [previewData]);

	const cardSpacing = Number(getLayoutValue("card_spacing", 16));
	const showRating = getLayoutValue("show_rating", true) !== false;
	const showAvatar = getLayoutValue("show_avatar", true) !== false;
	const accentColor = brandKit?.color_accent_hex || "#6366f1";
	const ratingStarColor = String(getLayoutValue("rating_star_color", "") || "") || undefined;

	return (
		<div style={fontStyle}>
			<TestimonialList
				testimonials={testimonials}
				cardSpacing={cardSpacing}
				showRating={showRating}
				showAvatar={showAvatar}
				accentColor={accentColor}
				ratingStarColor={ratingStarColor}
				onVideoPlay={setActiveVideoPlaybackId}
			/>

			{/* Load More Button */}
			{nextCursor && (
				<div className="flex justify-center py-6 sm:py-8">
					<button
						type="button"
						onClick={onLoadMore}
						disabled={isLoadingMore}
						className="inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-900"
					>
						{isLoadingMore ? (
							<>
								<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								Loading...
							</>
						) : (
							"Load More"
						)}
					</button>
				</div>
			)}

			{/* Powered by branding */}
			{showBranding && (
				<div className="py-4 flex justify-center text-center text-gray-400">
					<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
						<img src="/images/badge-sayaboutus.svg" alt="build social proof with Say About Us" className="h-5 w-auto" />
					</a>
				</div>
			)}

			{activeVideoPlaybackId && <VideoModal open={!!activeVideoPlaybackId} onOpenChange={(open) => !open && setActiveVideoPlaybackId(null)} playbackId={activeVideoPlaybackId} />}
		</div>
	);
}
